import { computed, DestroyRef, effect, inject, Injectable, InjectionToken, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { fromEvent, interval, merge, Subscription, timer } from 'rxjs';
import { debounceTime, filter, tap, throttleTime } from 'rxjs/operators';

import { Environment } from '@ld.consumer-portal/models';
import { AnalyticsService } from './analytics.service';
import { AuthService } from './auth.service';
import { LoggingService } from './logging.service';
import { ServisBotService } from './servisbot.service';
import { TokenRefreshService } from './token-refresh.service';

export const SESSION_CLEANUP_FN = new InjectionToken<() => void>('SESSION_CLEANUP_FN');

type IdleSyncMessage =
  | { type: 'SHOW_WARNING'; countDown: number }
  | { type: 'CONTINUE_SESSION' }
  | { type: 'LOGOUT' };

@Injectable({
  providedIn: 'root',
})
export class UserIdleService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly analytics = inject(AnalyticsService);
  private readonly authService = inject(AuthService);
  private readonly sessionCleanupFn = inject(SESSION_CLEANUP_FN, { optional: true });
  private readonly translocoService = inject(TranslocoService);
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly servisBotService = inject(ServisBotService);
  private readonly logger = inject(LoggingService);

  private env: Environment | null = null;

  private idleTimeMs = 1800 * 1000;

  private countDownTime = 300;

  private readonly _countDown = signal<number>(0);
  readonly countDown = this._countDown.asReadonly();

  private readonly _showWarning = signal(false);
  readonly showWarning = this._showWarning.asReadonly();

  private readonly _isWatching = signal(false);
  readonly isWatching = this._isWatching.asReadonly();

  isIdleLogout = false;

  readonly countDownFormatted = computed(() => {
    const seconds = this._countDown();
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  });

  private readonly LOGOUT_MSG_KEY = 'LOGOUT_MSG_KEY';

  private readonly TAB_WATCH_KEY = 'tabWatch';

  private guid = '';

  private readonly TOKEN_POLL_INTERVAL_MS = 10 * 1000;

  private readonly IDLE_CHANNEL_NAME = 'user-idle-sync';
  private idleChannel: BroadcastChannel | null = null;
  private isCrossTabAction = false;

  private idleSubscription: Subscription | null = null;
  private timerSubscription: Subscription | null = null;
  private countDownSubscription: Subscription | null = null;
  private tabWatchSubscription: Subscription | null = null;
  private tokenPollSubscription: Subscription | null = null;

  private readonly _isIdle = signal(false);

  private readonly idleEffect = effect(() => {
    const isIdle = this._isIdle();
    const isWatching = this._isWatching();
    if (isIdle && isWatching) {
      untracked(() => {
        this.analytics.trackSessionTimeout();
        this._countDown.set(this.countDownTime);
        this._showWarning.set(true);
        this.startCountDownTimer();
        this.broadcastMessage({ type: 'SHOW_WARNING', countDown: this.countDownTime });
      });
    }
  });

  private readonly warningEffect = effect(() => {
    const showWarning = this._showWarning();
    const isWatching = this._isWatching();
    if (showWarning && isWatching) {
      untracked(() => this.startTokenValidityPolling());
    } else {
      untracked(() => this.stopTokenValidityPolling());
    }
  });

  private isIdleTimerRunning = false;

  get logoutMessage(): string | null {
    return sessionStorage.getItem(this.LOGOUT_MSG_KEY);
  }

  set logoutMessage(message: string | null) {
    if (message) {
      sessionStorage.setItem(this.LOGOUT_MSG_KEY, message);
    } else {
      sessionStorage.removeItem(this.LOGOUT_MSG_KEY);
    }
  }

  initialize(env: Environment): void {
    this.env = env;
    this.idleTimeMs = (env.appSettings.logoutIdleTime ?? 1800) * 1000;
    this.countDownTime = env.appSettings.logoutCountDownTime ?? 300;
  }

  startWatching(): void {
    if (this._isWatching()) {
      return;
    }

    this._isWatching.set(true);
    this.isIdleLogout = false;

    this.guid = this.generateGuid();
    localStorage.setItem(this.TAB_WATCH_KEY, this.guid);

    const idle$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'click'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'DOMMouseScroll'),
      fromEvent(document, 'mousewheel'),
      fromEvent(document, 'touchmove'),
      fromEvent(document, 'MSPointerMove'),
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'resize'),
    );

    this.idleSubscription = idle$
      .pipe(
        tap(() => this.resetTimer()),
        throttleTime(10000),
        tap(() => {
          this.guid = this.generateGuid();
          localStorage.setItem(this.TAB_WATCH_KEY, this.guid);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.startIdleTimer();

    this.idleChannel = new BroadcastChannel(this.IDLE_CHANNEL_NAME);
    this.idleChannel.onmessage = (event: MessageEvent<IdleSyncMessage>) => {
      this.handleCrossTabMessage(event.data);
    };

    this.startTabWatch();
  }

  stopWatching(): void {
    this._isWatching.set(false);
    this._showWarning.set(false);
    this._countDown.set(0);

    this.idleSubscription?.unsubscribe();
    this.timerSubscription?.unsubscribe();
    this.countDownSubscription?.unsubscribe();
    this.tabWatchSubscription?.unsubscribe();
    this.tokenPollSubscription?.unsubscribe();

    this.idleSubscription = null;
    this.timerSubscription = null;
    this.countDownSubscription = null;
    this.tabWatchSubscription = null;
    this.tokenPollSubscription = null;

    this.isIdleTimerRunning = false;
    this._isIdle.set(false);

    if (this.idleChannel) {
      this.idleChannel.onmessage = null;
      this.idleChannel.close();
      this.idleChannel = null;
    }
  }

  async continueSession(): Promise<void> {
    this.analytics.trackSessionContinue();
    this._showWarning.set(false);
    this._countDown.set(0);
    this.countDownSubscription?.unsubscribe();
    this.countDownSubscription = null;
    this.stopTokenValidityPolling();
    this._isIdle.set(false);

    try {
      await this.tokenRefreshService.forceRefresh();
    } catch {
      // Refresh failed — will retry below
    }

    if (this.authService.isTokenExpired()) {
      this.logger.logWarning('[UserIdleService] Token still expired after first refresh attempt, retrying...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      try {
        await this.tokenRefreshService.forceRefresh();
      } catch {
        // Second attempt also failed
      }
    }

    if (this.authService.isTokenExpired()) {
      this.logger.logWarning('[UserIdleService] Token still expired after continueSession refresh attempts, redirecting to login');
      await this.logoutDueToExpiredToken();
      return;
    }

    this.broadcastMessage({ type: 'CONTINUE_SESSION' });
    this.resetTimer();
  }

  async logoutDueToInactivity(): Promise<void> {
    this.analytics.trackSessionExpired();
    this.logoutMessage = this.translocoService.translate('servicesMessagesSessionLoggedOutInactivity');
    this.isIdleLogout = true;

    this.broadcastMessage({ type: 'LOGOUT' });
    this.stopWatching();

    this.clearSession();

    try {
      await this.servisBotService.clear();
    } catch (error) {
      this.logger.logException(error, 'Failed to clear ServisBot during idle logout');
    }
    document.body.classList.remove('servisbot-enabled');

    await this.router.navigate(['/sign-out']);
  }

  private async logoutDueToExpiredToken(): Promise<void> {
    this.isIdleLogout = true;
    this.analytics.trackSessionExpired();
    this.broadcastMessage({ type: 'LOGOUT' });
    this.stopWatching();
    this.clearSession();

    try {
      await this.servisBotService.clear();
    } catch (error) {
      this.logger.logException(error, 'Failed to clear ServisBot during token expiry logout');
    }
    document.body.classList.remove('servisbot-enabled');

    try {
      await this.router.navigate(['/sign-out']);
    } catch (navError) {
      this.logger.logException(navError, 'Failed to navigate to login during token expiry logout');
    }
  }

  private clearSession(): void {
    this.sessionCleanupFn?.();
    this.authService.clearAllTokens();

    const logoutMsg = this.logoutMessage;
    sessionStorage.clear();
    if (logoutMsg) {
      this.logoutMessage = logoutMsg;
    }

    localStorage.removeItem('ciam_session_active');
    localStorage.removeItem('native_ciam_refresh_token');
    // Keep native_ciam_auth_options for biometric silent refresh after app restart
  }

  private startIdleTimer(): void {
    if (this.isIdleTimerRunning) {
      return;
    }

    this.isIdleTimerRunning = true;
    this.timerSubscription = timer(this.idleTimeMs, this.idleTimeMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const currentGuid = localStorage.getItem(this.TAB_WATCH_KEY);
        if (currentGuid !== this.guid) {
          return;
        }
        this._isIdle.set(true);
      });
  }

  private resetTimer(): void {
    if (this._showWarning()) {
      return;
    }

    this.timerSubscription?.unsubscribe();
    this.timerSubscription = null;
    this.isIdleTimerRunning = false;
    this.startIdleTimer();
  }

  private startCountDownTimer(): void {
    this.countDownSubscription?.unsubscribe();
    this.countDownSubscription = timer(1000, 1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (tick) => {
        const remaining = this.countDownTime - tick - 1;
        this._countDown.set(remaining);

        if (remaining <= 0) {
          await this.logoutDueToInactivity();
        }
      });
  }

  private async startTokenValidityPolling(): Promise<void> {
    this.stopTokenValidityPolling();

    if (this.authService.isTokenExpired() || this.authService.isTokenExpiringSoon(300)) {
      try {
        await this.tokenRefreshService.forceRefresh();
      } catch {
        // Refresh failed — polling will handle token expiry
      }
    }

    this.tokenPollSubscription = interval(this.TOKEN_POLL_INTERVAL_MS)
      .pipe(
        filter(() => this._showWarning()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.authService.isTokenExpired() || this.authService.isTokenExpiringSoon(300)) {
          this.tokenRefreshService.forceRefresh().catch(() => {
            // Refresh failed — countdown timer will handle logout
          });
        }
      });
  }

  private stopTokenValidityPolling(): void {
    this.tokenPollSubscription?.unsubscribe();
    this.tokenPollSubscription = null;
  }

  private startTabWatch(): void {
    this.tabWatchSubscription = fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter((event) => event.key === this.TAB_WATCH_KEY),
        debounceTime((this.idleTimeMs / 1000 + this.countDownTime) * 1000),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const currentGuid = localStorage.getItem(this.TAB_WATCH_KEY);
        if (currentGuid !== this.guid) {
          this.logoutDueToInactivity();
        }
      });
  }

  private handleCrossTabMessage(msg: IdleSyncMessage): void {
    this.isCrossTabAction = true;
    try {
      switch (msg.type) {
        case 'SHOW_WARNING':
          if (this._isWatching() && !this._showWarning()) {
            this._countDown.set(msg.countDown);
            this._showWarning.set(true);
            this.startCountDownTimer();
            this.startTokenValidityPolling();
          }
          break;

        case 'CONTINUE_SESSION':
          if (this._showWarning()) {
            this._showWarning.set(false);
            this._countDown.set(0);
            this.countDownSubscription?.unsubscribe();
            this.countDownSubscription = null;
            this.stopTokenValidityPolling();
            this._isIdle.set(false);
            this.resetTimer();
          }
          break;

        case 'LOGOUT':
          if (this._isWatching()) {
            this.logoutDueToInactivity();
          }
          break;
      }
    } finally {
      this.isCrossTabAction = false;
    }
  }

  private broadcastMessage(msg: IdleSyncMessage): void {
    if (this.isCrossTabAction) return;
    try {
      this.idleChannel?.postMessage(msg);
    } catch {
      // Channel may be closed
    }
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
